import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from '../../../app/LanguageContext';
import { getCatalogSettings, updateCatalogSettings } from '../../../app/api';
import MediaGallery from '../../../modules/medias/public/MediaGallery';

const SOCIAL_NETWORKS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'other', label: 'Outro' },
];

const emptyPhone = () => ({ label: '', number: '', is_whatsapp: false });
const emptySocial = () => ({ network: 'instagram', label: '', url: '' });

const LOGO_METADATA_VALUE = 'logo';

const defaultSettings = () => ({
  logo_media_id: '',
  company_name: '',
  slogan: '',
  email: '',
  website: '',
  address: '',
  phones: [],
  socials: [],
  footer_note: '',
});

const CatalogSettingsPage = () => {
  const { translate } = useTranslation();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);
  const mediaGalleryBootstrapped = useRef(false);
  const logoMediaIdRef = useRef('');
  const settingsRef = useRef(settings);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const load = useCallback(async () => {
    setLoading(true);
    mediaGalleryBootstrapped.current = false;
    try {
      const res = await getCatalogSettings();
      const data = res?.data ?? res ?? {};
      const next = {
        ...defaultSettings(),
        ...data,
        phones: Array.isArray(data.phones) ? data.phones : [],
        socials: Array.isArray(data.socials) ? data.socials : [],
      };
      logoMediaIdRef.current = next.logo_media_id || '';
      setSettings(next);
      setLogoPreviewUrl(data.logo_url || '');
      setGalleryRefreshKey((k) => k + 1);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (fields) => {
    setSettings((prev) => {
      const next = { ...prev, ...fields };
      if (Object.prototype.hasOwnProperty.call(fields, 'logo_media_id')) {
        logoMediaIdRef.current = fields.logo_media_id || '';
      }
      return next;
    });
  };

  const handleLogoMediaChange = async (medias) => {
    const first = (medias || [])[0];
    const nextId = first?.id || '';

    if (!mediaGalleryBootstrapped.current) {
      mediaGalleryBootstrapped.current = true;
      if (!nextId && logoMediaIdRef.current) {
        return;
      }
    }

    if (nextId === logoMediaIdRef.current) {
      return;
    }

    patch({ logo_media_id: nextId });
    if (first?.file_path) {
      const path = first.file_path.startsWith('http') ? first.file_path : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${first.file_path.startsWith('/') ? '' : '/'}${first.file_path}`;
      setLogoPreviewUrl(path);
    } else if (!nextId) {
      setLogoPreviewUrl('');
    }

    try {
      const current = settingsRef.current;
      const res = await updateCatalogSettings({
        ...current,
        logo_media_id: nextId,
        phones: current.phones
          .map((p) => ({
            label: (p.label || '').trim(),
            number: (p.number || '').trim(),
            is_whatsapp: Boolean(p.is_whatsapp),
          }))
          .filter((p) => p.number),
        socials: current.socials
          .map((s) => ({
            network: (s.network || '').trim(),
            label: (s.label || '').trim(),
            url: (s.url || '').trim(),
          }))
          .filter((s) => s.url || s.label),
      });
      const data = res?.data ?? res ?? {};
      logoMediaIdRef.current = data.logo_media_id || nextId;
      if (data.logo_url) setLogoPreviewUrl(data.logo_url);
      setSnackbar({
        open: true,
        message: nextId
          ? translate('catalog.settings.logo_saved')
          : translate('catalog.settings.logo_removed'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const updatePhone = (idx, fields) => {
    setSettings((prev) => {
      const next = [...prev.phones];
      next[idx] = { ...next[idx], ...fields };
      return { ...prev, phones: next };
    });
  };

  const addPhone = () => {
    setSettings((prev) => ({ ...prev, phones: [...prev.phones, emptyPhone()] }));
  };

  const removePhone = (idx) => {
    setSettings((prev) => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== idx),
    }));
  };

  const updateSocial = (idx, fields) => {
    setSettings((prev) => {
      const next = [...prev.socials];
      next[idx] = { ...next[idx], ...fields };
      return { ...prev, socials: next };
    });
  };

  const addSocial = () => {
    setSettings((prev) => ({ ...prev, socials: [...prev.socials, emptySocial()] }));
  };

  const removeSocial = (idx) => {
    setSettings((prev) => ({
      ...prev,
      socials: prev.socials.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        phones: settings.phones
          .map((p) => ({
            label: (p.label || '').trim(),
            number: (p.number || '').trim(),
            is_whatsapp: Boolean(p.is_whatsapp),
          }))
          .filter((p) => p.number),
        socials: settings.socials
          .map((s) => ({
            network: (s.network || '').trim(),
            label: (s.label || '').trim(),
            url: (s.url || '').trim(),
          }))
          .filter((s) => s.url || s.label),
      };
      const res = await updateCatalogSettings(payload);
      const data = res?.data ?? res ?? {};
      setSettings({
        ...defaultSettings(),
        ...data,
        phones: Array.isArray(data.phones) ? data.phones : [],
        socials: Array.isArray(data.socials) ? data.socials : [],
      });
      logoMediaIdRef.current = data.logo_media_id || '';
      setLogoPreviewUrl(data.logo_url || '');
      setGalleryRefreshKey((k) => k + 1);
      setSnackbar({
        open: true,
        message: translate('catalog.settings.saved'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', pb: 6 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('catalog.settings.subtitle')}
      </Typography>

      {/* Brand block */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {translate('catalog.settings.section_brand')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              {translate('catalog.settings.logo_label')}
            </Typography>
            {logoPreviewUrl && (
              <Box
                component="img"
                src={logoPreviewUrl}
                alt={settings.company_name || translate('catalog.settings.logo_label')}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 72,
                  objectFit: 'contain',
                  mb: 1.5,
                  display: 'block',
                }}
              />
            )}
            <MediaGallery
              metadataKey="catalog_settings_logo"
              metadataValue={LOGO_METADATA_VALUE}
              metadataLinkUrl="/catalogs/settings"
              accept="image/*"
              maxFiles={1}
              refreshKey={galleryRefreshKey}
              onMediaChange={handleLogoMediaChange}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {translate('catalog.settings.logo_hint')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={translate('catalog.settings.company_name')}
                value={settings.company_name}
                onChange={(e) => patch({ company_name: e.target.value })}
                placeholder={translate('catalog.settings.company_name_placeholder')}
              />
              <TextField
                fullWidth
                label={translate('catalog.settings.slogan')}
                value={settings.slogan}
                onChange={(e) => patch({ slogan: e.target.value })}
                placeholder={translate('catalog.settings.slogan_placeholder')}
                helperText={translate('catalog.settings.slogan_hint')}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                label={translate('catalog.settings.footer_note')}
                value={settings.footer_note}
                onChange={(e) => patch({ footer_note: e.target.value })}
                placeholder={translate('catalog.settings.footer_note_placeholder')}
                helperText={translate('catalog.settings.footer_note_hint')}
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Contact */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {translate('catalog.settings.section_contact')}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {translate('catalog.settings.contact_hint')}
        </Typography>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label={translate('catalog.settings.email')}
            value={settings.email}
            onChange={(e) => patch({ email: e.target.value })}
            placeholder="contato@empresa.com.br"
            type="email"
          />
          <TextField
            fullWidth
            label={translate('catalog.settings.website')}
            value={settings.website}
            onChange={(e) => patch({ website: e.target.value })}
            placeholder="www.empresa.com.br"
            InputProps={{
              startAdornment: (
                <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label={translate('catalog.settings.address')}
            value={settings.address}
            onChange={(e) => patch({ address: e.target.value })}
            placeholder={translate('catalog.settings.address_placeholder')}
            helperText={translate('catalog.settings.address_hint')}
          />

          <Divider sx={{ my: 1 }} />

          {/* Phones */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {translate('catalog.settings.phones')}
            </Typography>
            {settings.phones.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {translate('catalog.settings.phones_empty')}
              </Typography>
            )}
            <Stack spacing={1.5}>
              {settings.phones.map((phone, idx) => (
                <Box
                  key={`phone-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 1fr) minmax(180px, 1.4fr) auto auto' },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    label={translate('catalog.settings.phone_label')}
                    value={phone.label}
                    onChange={(e) => updatePhone(idx, { label: e.target.value })}
                    placeholder={translate('catalog.settings.phone_label_placeholder')}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={translate('catalog.settings.phone_number')}
                    value={phone.number}
                    onChange={(e) => updatePhone(idx, { number: e.target.value })}
                    placeholder="(00) 00000-0000"
                    InputProps={{
                      startAdornment: phone.is_whatsapp ? (
                        <WhatsAppIcon fontSize="small" sx={{ mr: 1, color: '#25D366' }} />
                      ) : (
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={phone.is_whatsapp}
                        onChange={(e) => updatePhone(idx, { is_whatsapp: e.target.checked })}
                      />
                    }
                    label="WhatsApp"
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removePhone(idx)}
                    aria-label={translate('common.delete')}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addPhone}
              disabled={settings.phones.length >= 6}
              sx={{ mt: 1.5 }}
            >
              {translate('catalog.settings.phones_add')}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Social */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {translate('catalog.settings.section_social')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {translate('catalog.settings.social_hint')}
        </Typography>
        {settings.socials.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {translate('catalog.settings.social_empty')}
          </Typography>
        )}
        <Stack spacing={1.5}>
          {settings.socials.map((social, idx) => (
            <Box
              key={`social-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 0.8fr) minmax(140px, 1fr) minmax(180px, 1.6fr) auto' },
                gap: 1,
              }}
            >
              <TextField
                size="small"
                select
                fullWidth
                label={translate('catalog.settings.social_network')}
                value={social.network}
                onChange={(e) => updateSocial(idx, { network: e.target.value })}
              >
                {SOCIAL_NETWORKS.map((n) => (
                  <MenuItem key={n.value} value={n.value}>
                    {n.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                fullWidth
                label={translate('catalog.settings.social_label')}
                value={social.label}
                onChange={(e) => updateSocial(idx, { label: e.target.value })}
                placeholder="@minhaempresa"
              />
              <TextField
                size="small"
                fullWidth
                label={translate('catalog.settings.social_url')}
                value={social.url}
                onChange={(e) => updateSocial(idx, { url: e.target.value })}
                placeholder="https://instagram.com/minhaempresa"
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => removeSocial(idx)}
                aria-label={translate('common.delete')}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addSocial}
          disabled={settings.socials.length >= 8}
          sx={{ mt: 1.5 }}
        >
          {translate('catalog.settings.social_add')}
        </Button>
      </Paper>

      {/* Sticky save */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={load} disabled={saving} variant="text">
          {translate('common.cancel')}
        </Button>
        <Button onClick={save} disabled={saving} variant="contained">
          {saving ? <CircularProgress size={20} /> : translate('common.save')}
        </Button>
      </Box>

      {settings.logo_media_id && (
        <Box sx={{ mt: 2 }}>
          <Chip
            size="small"
            label={`${translate('catalog.settings.logo_active')} · ${settings.logo_media_id}`}
            variant="outlined"
            onDelete={() => patch({ logo_media_id: '' })}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CatalogSettingsPage;
